/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { Suspense, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router'
import { activityApiService } from '~/services/api/api.activity'
import { useTranslation } from 'react-i18next'
import { RcmTableDataWrapper } from '~/components/rcm-table'
import { baseApiService } from '~/services/api/base'
import RcmTableFilterTrigger from '~/components/rcm-table/RcmTableFilterTrigger'
import TableFilter from './TableFilter'
import LoadingSpinner from '~/components/LoadingSpinner'
import FsiActivityTable from './Table'
import { SystemFolderType } from '~/constants/fsi'

interface FileItem {
  id?: number
  name?: string
  documentUID?: number
  path?: Array<{ id?: number; name?: string }>
  systemFolderType?: string
  hasNoACLToRCRoot?: boolean
  obsolete?: boolean
}

const FsiActivity: React.FC = () => {
  const persistenceKey = 'fsiActivity'
  const { t: translate } = useTranslation()
  const { id } = useParams()
  const fsiId = id ? parseInt(id) : 1
  const [currentFolder, setCurrentFolder] = useState<FileItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (fsiId) {
      setIsLoading(true)
      // Call getFile with pageSize=1 to get complete file info including documentUID and path
      baseApiService
        .fetch<any>(`/api/files/${fsiId}`, {
          params: { pageSize: 1 }
        })
        .then((data: any) => {
          const folder = {
            ...data,
            name: data.name === '/' ? translate('BROWSER_ROOT_FOLDER_NAME') : data.name
          }
          setCurrentFolder(folder)
          setIsLoading(false)
        })
        .catch((error: any) => {
          if (error?.status === 403 && fsiId === 1) {
            setCurrentFolder({
              name: translate('BROWSER_ROOT_FOLDER_NAME'),
              hasNoACLToRCRoot: true
            })
          }
          setIsLoading(false)
        })
    }
  }, [fsiId, translate])

  const loadData = async (queryParams: any, filter: any) => {
    // Only load if we have currentFolder and documentUID
    if (!currentFolder || !currentFolder.documentUID) {
      return {
        elements: [],
        totalElements: 0,
        hasNextPage: false
      }
    }

    const params: any = { ...queryParams }
    params.docuId = currentFolder.documentUID

    // Filter already contains arrays of values (converted in TableFilter)
    if (filter?.actions && filter.actions.length > 0) {
      params.action = filter.actions
    }

    if (filter?.users && filter.users.length > 0) {
      params.createdById = filter.users
    }

    if (filter?.includeSystemGeneratedEvents !== undefined) {
      params.includeSystemGeneratedEvents = filter.includeSystemGeneratedEvents
    }

    if (filter?.from instanceof Date) {
      params.createdAtFrom = filter.from.getTime()
    } else if (filter?.from) {
      params.createdAtFrom = undefined
    }

    if (filter?.to instanceof Date) {
      params.createdAtTo = filter.to.getTime()
    } else if (filter?.to) {
      params.createdAtTo = undefined
    }

    const result = await activityApiService.getAllAuditLogs(params)
    return {
      elements: result.elements || [],
      totalElements: result.totalElements ?? 0,
      hasNextPage: result.hasNextPage
    }
  }

  if (isLoading) {
    return (
      <div className="mcc-content-frame theme-sandman">
        <div className="system-container">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  return (
    <div className="mcc-content-frame theme-sandman">
      <div className="system-container fsi-activity-page">
        <Suspense fallback={<LoadingSpinner />}>
          <RcmTableDataWrapper
            loadData={loadData}
            defaultSortBy={'createdAt'}
            defaultSortDesc={true}
            persistenceKey={persistenceKey}
            doPreload={true}
          >
            <div className="sub-page-header">
              <div className="d-flex flex-wrap">
                <div className="align-left d-flex flex-column align-items-baseline gap-1">
                  <h1>{translate('BROWSER_FSI_ACTIVITY_TITLE')}</h1>

                  {/* Breadcrumb */}
                  {currentFolder && (
                    <div className="breadcrumbs-path">
                      {/* Path segments */}
                      {currentFolder.path?.map((pathItem, index) => (
                        <span key={pathItem.id || index}>
                          {currentFolder.systemFolderType === SystemFolderType.WORKFLOWS ? (
                            // For WORKFLOWS, show path as plain text
                            <span className="current">{pathItem.name}&nbsp;/</span>
                          ) : (
                            // For other types, show as clickable link
                            <span>
                              <Link to={`/browser/${pathItem.id}`}>{pathItem.name}</Link>
                              &nbsp;/
                            </span>
                          )}
                        </span>
                      ))}

                      {/* Current folder/file name */}
                      {!currentFolder.hasNoACLToRCRoot && (
                        <Link to={`/show-file/${currentFolder.id}`}>
                          <span>{currentFolder.name}</span>
                          &nbsp;
                          {currentFolder.obsolete && <b className="obsolete-text-color">{translate('OBSOLETE')}</b>}
                        </Link>
                      )}
                    </div>
                  )}
                </div>
                <div className="align-right align-bottom d-flex">
                  <RcmTableFilterTrigger />
                </div>
              </div>
            </div>

            <div>
              <TableFilter fsiId={fsiId} />
              <FsiActivityTable />
            </div>
          </RcmTableDataWrapper>
        </Suspense>
      </div>
    </div>
  )
}

export default FsiActivity
